/**
 * Helper functions for Project Locations component
 * These are extracted for testing and reusability
 */

import { ProjectLocationFilters } from './Locations.types';

/**
 * Initializes project location filters with correct default state.
 * CRITICAL: This keeps filter arrays empty initially to avoid "all selected by default" bug.
 * Filter arrays should only be populated by user interaction, not from API payload.
 * 
 * @param existingFilters - Current filter state
 * @param payload - API response payload containing available options and form values
 * @returns Updated filter state with arrays reset to empty
 */
export const initializeProjectLocationFilters = (
    existingFilters: ProjectLocationFilters,
    payload: any
): ProjectLocationFilters => {
    return {
        ...existingFilters,
        // Only set non-array fields from payload to avoid confusion between available options and selected values
        generation: payload.generation || '',
        locationAbbreviations: payload.locationAbbreviations || '',
        locationName: payload.locationName || '',
        selectedStatus: payload.selectedStatus || 0,
        isOnHold: payload.isOnHold,
        isActive: payload.isActive,
        // Keep selected arrays empty initially - they will be populated by user interaction
        projectZeroDates: [],
        selectedBaselines: [],
        selectedProjectManagers: [],
        brands: [],
        continents: [],
        managementCompanies: [],
        areas: [],
        regions: [],
        types: [],
        countries: [],
    };
};

/**
 * Formats a date to "D MMM YYYY" format (e.g., "5 Feb 2026")
 */
export const formatDate = (dateInput: Date | string): string => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
};

/**
 * Converts abbreviations string to array, supporting both comma and space delimiters
 * @param abbreviations - String like "NY, LA, SF" or "NY LA SF"
 * @returns Array of abbreviations
 */
export const abbreviationsToArray = (abbreviations: string | null | undefined) => {
    if (!abbreviations) {
        return [];
    }
    return String(abbreviations)
        .split(/[,\s]+/)
        .map((token) => token.trim())
        .filter((token) => token.length > 0);
};

/**
 * Status filter options for dropdown
 */
export const STATUS_FILTER_OPTIONS = [
    { label: 'All', value: 0 },
    { label: 'Published', value: 1 },
    { label: 'Draft', value: 2 },
] as const;

/**
 * Active/inactive filter options for dropdown
 */
export const ACTIVE_FILTER_OPTIONS = [
    { label: 'All', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false },
] as const;
